/**
 * Обновление приложения: проверка только при старте.
 * Единый источник правды — version.json (buildId).
 * Web / VK / Telegram: один раз при загрузке → если buildId отличается → reload.
 */
export const VERSION_CHECK_PATH = '/version.json';
/** Имя файла в dist (для vite-плагинов записи version.json). */
export const VERSION_JSON_FILENAME = 'version.json';
const VERSION_QUERY_PARAM = '__v';
async function getServerBuildId() {
    try {
        const res = await fetch(`${VERSION_CHECK_PATH}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok)
            return null;
        const data = (await res.json());
        const serverBuildId = data?.buildId != null ? String(data.buildId) : '';
        return serverBuildId || null;
    }
    catch {
        return null;
    }
}
function canCheckVersion(currentBuildId) {
    return !!currentBuildId && currentBuildId !== 'unknown' && typeof window !== 'undefined' && typeof document !== 'undefined';
}
function buildReloadUrl(buildId) {
    const url = new URL(window.location.href);
    url.searchParams.set(VERSION_QUERY_PARAM, buildId);
    return url.toString();
}
async function resolveNewBuildId(currentBuildId) {
    if (!canCheckVersion(currentBuildId))
        return null;
    const serverBuildId = await getServerBuildId();
    return serverBuildId && serverBuildId !== currentBuildId ? serverBuildId : null;
}
export async function hasNewAppVersion(currentBuildId) {
    return (await resolveNewBuildId(currentBuildId)) != null;
}
/**
 * Проверяет версию приложения один раз при старте.
 * Запрос version.json (no-cache); при отличии buildId — reload.
 * Возвращает true если reload запущен (рендер не нужен), false — версии совпадают или сеть недоступна.
 * Вызывать до рендера React: await checkAppVersion(...); если true — дальше не идти.
 */
export async function checkAppVersion(currentBuildId) {
    if (!canCheckVersion(currentBuildId))
        return false;
    const newBuildId = await resolveNewBuildId(currentBuildId);
    if (!newBuildId)
        return false;
    try {
        // Показываем «Обновление...» в статичном лоадере (если он есть) перед reload
        const loaderText = document.getElementById('app-static-loader-text');
        if (loaderText)
            loaderText.textContent = 'Обновление...';
        window.location.replace(buildReloadUrl(newBuildId));
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Фоновая проверка версии для уже открытой вкладки/PWA.
 * По умолчанию: раз в 60 сек + при возврате на вкладку.
 * Возвращает функцию остановки watcher.
 */
export function startAppVersionWatcher(currentBuildId, options) {
    if (!canCheckVersion(currentBuildId))
        return () => { };
    const intervalMs = options?.intervalMs ?? 60000;
    const checkOnVisibility = options?.checkOnVisibility ?? true;
    let stopped = false;
    let updateTriggered = false;
    const triggerUpdate = async (newBuildId) => {
        if (updateTriggered || stopped)
            return;
        updateTriggered = true;
        if (options?.onUpdate) {
            await options.onUpdate(newBuildId);
            return;
        }
        window.location.replace(buildReloadUrl(newBuildId));
    };
    const tick = async () => {
        if (stopped || updateTriggered)
            return;
        const newBuildId = await resolveNewBuildId(currentBuildId);
        if (newBuildId) {
            await triggerUpdate(newBuildId);
        }
    };
    void tick();
    const timer = window.setInterval(() => {
        void tick();
    }, intervalMs);
    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            void tick();
        }
    };
    if (checkOnVisibility) {
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return () => {
        stopped = true;
        window.clearInterval(timer);
        if (checkOnVisibility) {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        }
    };
}
export const RELEASE_NOTES_PATH = '/release-notes.json';
export const RELEASE_NOTES_STORAGE_KEY = 'mb:last-seen-release-id';
const SHOWABLE_RELEASE_MODES = ['feature', 'major'];
function isValidStringArray(input) {
    return Array.isArray(input) && input.every((item) => typeof item === 'string' && item.trim().length > 0);
}
function normalizeReleaseNotes(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const data = raw;
    const releaseId = typeof data.releaseId === 'string' ? data.releaseId.trim() : '';
    if (!releaseId)
        return null;
    const mode = typeof data.mode === 'string' ? data.mode : 'silent';
    const features = isValidStringArray(data.features) ? data.features.map((v) => v.trim()).filter(Boolean) : [];
    const changes = isValidStringArray(data.changes) ? data.changes.map((v) => v.trim()).filter(Boolean) : [];
    return {
        releaseId,
        buildId: typeof data.buildId === 'string' ? data.buildId : undefined,
        mode,
        showInApp: Boolean(data.showInApp),
        mandatory: Boolean(data.mandatory),
        title: typeof data.title === 'string' ? data.title : 'Что нового',
        featuresTitle: typeof data.featuresTitle === 'string' ? data.featuresTitle : 'Новые фичи',
        changesTitle: typeof data.changesTitle === 'string' ? data.changesTitle : 'Изменения',
        features,
        changes,
    };
}
export async function fetchReleaseNotes(path = RELEASE_NOTES_PATH) {
    try {
        const response = await fetch(`${path}?t=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok)
            return null;
        const data = await response.json();
        return normalizeReleaseNotes(data);
    }
    catch {
        return null;
    }
}
export function shouldShowReleaseNotes(notes) {
    if (!notes || !notes.releaseId)
        return false;
    const mode = notes.mode ?? 'silent';
    if (!SHOWABLE_RELEASE_MODES.includes(mode))
        return false;
    if (!notes.showInApp)
        return false;
    return true;
}
export function markReleaseNotesSeen(releaseId, key = RELEASE_NOTES_STORAGE_KEY) {
    if (!releaseId || typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(key, releaseId);
    }
    catch {
        // ignore localStorage errors
    }
}
export function getSeenReleaseId(key = RELEASE_NOTES_STORAGE_KEY) {
    if (typeof window === 'undefined')
        return '';
    try {
        return window.localStorage.getItem(key) ?? '';
    }
    catch {
        return '';
    }
}
export async function getPendingReleaseNotes(key = RELEASE_NOTES_STORAGE_KEY) {
    const notes = await fetchReleaseNotes();
    if (!shouldShowReleaseNotes(notes))
        return null;
    const seenReleaseId = getSeenReleaseId(key);
    if (seenReleaseId && seenReleaseId === notes.releaseId)
        return null;
    return notes;
}
//# sourceMappingURL=ensureFreshVersion.js.map